/**
 * Curve-wide scenery macro (M13) — the off-grid curve-wide family enters
 * as a SET-PIECE: four quarter-turn wide arcs close into a freestanding
 * ring (a roundabout display) on open ground beside the track. The family
 * cannot mate with the 1-unit port lattice (ports on ±0.85-style bbox
 * faces, lane non-concentric) — but it mates perfectly with ITSELF, so
 * the ring is self-closed: every port flush, nothing juts (composition
 * honesty). Solid variants make a filled plaza; the small size has no
 * solid form and always builds open.
 *
 * Measured (tools/measure_ports.ts): each variant's ports sit centered on
 * its x− and z− faces; rotations [0, 3, 2, 1] at ring-center offsets
 * (+h,+h), (−h,+h), (−h,−h), (+h,−h) mate all four in a closed circuit.
 */
import type { Placement } from "../shared/types.ts";
import type { Rng } from "./rng.ts";

const RING_CHANCE = 0.3;

interface Variant {
  id: string;
  solidId?: string;
  /** Piece bbox half-extent (ring pieces sit ±h from the ring center). */
  h: number;
}

const VARIANTS: readonly Variant[] = [
  { id: "curve-wide", h: 0.85 },
  { id: "curve-wide-medium", solidId: "curve-wide-solid-medium", h: 1.35 },
  { id: "curve-wide-large", solidId: "curve-wide-solid-large", h: 1.85 },
];

/** Piece center offsets (units of h) and rotations that close the ring. */
const CORNERS: readonly (readonly [number, number, number])[] = [
  [1, 1, 0],
  [-1, 1, 3],
  [-1, -1, 2],
  [1, -1, 1],
];

export interface SceneryPlan {
  placements: Placement[];
  cells: string[];
}

export function planSceneryRing(
  rng: Rng,
  trackCells: ReadonlySet<string>,
): SceneryPlan {
  const empty: SceneryPlan = { placements: [], cells: [] };
  if (!rng.chance(RING_CHANCE)) return empty;

  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const cell of trackCells) {
    const [x, z] = cell.split(",").map(Number);
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
  }
  if (!Number.isFinite(minX)) return empty;

  // Pick the variant first (bigger rings preferred when they fit), then
  // search ring centers on cell corners around the track: the whole
  // footprint block must be clear of track, and the ring must stand near
  // the line (visible, not fog-lost) without touching it.
  const order = rng.shuffle(
    VARIANTS.flatMap((v) => (v.h > 1 ? [v, v] : [v])),
  );
  for (const variant of order) {
    const reach = variant.h * 2; // footprint half-extent around the center
    const cellsFor = (rx: number, rz: number): string[] | null => {
      const out: string[] = [];
      let nearTrack = false;
      for (
        let cx = Math.floor(rx - reach);
        cx <= Math.ceil(rx + reach);
        cx++
      ) {
        for (
          let cz = Math.floor(rz - reach);
          cz <= Math.ceil(rz + reach);
          cz++
        ) {
          if (trackCells.has(`${cx},${cz}`)) return null;
          out.push(`${cx},${cz}`);
        }
      }
      // Proximity: some track cell within reach+4 of the center.
      outer:
      for (let dx = -Math.ceil(reach + 4); dx <= reach + 4; dx++) {
        for (let dz = -Math.ceil(reach + 4); dz <= reach + 4; dz++) {
          if (trackCells.has(`${Math.round(rx + dx)},${Math.round(rz + dz)}`)) {
            nearTrack = true;
            break outer;
          }
        }
      }
      return nearTrack ? out : null;
    };

    const candidates: [number, number][] = [];
    for (let x = minX - 8; x <= maxX + 8; x++) {
      for (let z = minZ - 8; z <= maxZ + 8; z++) {
        if (cellsFor(x + 0.5, z + 0.5) !== null) candidates.push([x, z]);
      }
    }
    if (candidates.length === 0) continue;

    const [gx, gz] = rng.pick(
      candidates.sort((a, b) => a[0] - b[0] || a[1] - b[1]),
    );
    const rx = gx + 0.5;
    const rz = gz + 0.5;
    const solid = variant.solidId !== undefined && rng.chance(0.5);
    // TWO-TIER ROTUNDA (M19c): measured — open and solid curve-wide
    // forms are BOTH exactly 0.5 tall with identical footprints, so a
    // solid ring is a plinth an open ring stacks on flush (self-mating
    // in plan, flush in elevation; no falsework needed). Appended child
    // draw: the solid coin above stays byte-identical.
    const rotunda = solid && rng.child("rotunda").chance(0.4);
    const id = solid ? variant.solidId! : variant.id;
    const placements: Placement[] = CORNERS.map(([sx, sz, r]) => ({
      pieceId: id,
      pos: [rx + sx * variant.h, 0, rz + sz * variant.h],
      rotation: r as Placement["rotation"],
      role: "decor" as const,
    }));
    if (rotunda) {
      placements.push(...CORNERS.map(([sx, sz, r]): Placement => ({
        pieceId: variant.id,
        pos: [rx + sx * variant.h, 0.5, rz + sz * variant.h],
        rotation: r as Placement["rotation"],
        role: "decor",
      })));
    }
    return { placements, cells: cellsFor(rx, rz)! };
  }
  return empty;
}
